import { Theme } from 'theme-ui';
import {
  appContainerWidth,
  desktopSidebarWidth,
  mobileBreakpoint,
  navHeight,
  tabletBreakpoint,
} from './index';

const theme = {
  useColorSchemeMediaQuery: true,

  fonts: {
    body: 'Inter, system-ui, sans-serif',
    heading: 'Inter, system-ui, sans-serif',
  },

  colors: {
    blue: 'rgba(42, 63, 251, 1)',
    blue90: 'rgba(42, 63, 251, 0.9)',
    blue80: 'rgba(42, 63, 251, 0.8)',
    blue70: 'rgba(42, 63, 251, 0.7)',
    blue60: 'rgba(42, 63, 251, 0.6)',
    blue20: 'rgba(42, 63, 251, 0.4)',
    blue10: 'rgba(42, 63, 251, 0.1)',
    blueDark: 'rgba(0, 194, 255, 1)',
    green: 'rgba(67, 225, 143, 1)',
    surfaceGray: 'rgba(242, 247, 252, 1)',
    surfaceGrayDark: 'rgba(22, 23, 27, 1)',
    buttonGray: 'rgba(228, 235, 241, 1)',
    buttonGrayDark: 'rgba(36, 37, 45, 1)',
    black: 'rgba(0, 0, 0, 1)',
    black80: 'rgba(0, 0, 0, 0.8)',
    black70: 'rgba(0, 0, 0, 0.7)',
    black60: 'rgba(0, 0, 0, 0.6)',
    black50: 'rgba(0, 0, 0, 0.5)',
    black30: 'rgba(0, 0, 0, 0.3)',
    black20: 'rgba(0, 0, 0, 0.2)',
    black10: 'rgba(0, 0, 0, 0.1)',
    white: 'rgba(255, 255, 255, 1)',
    white90: 'rgba(255, 255, 255, 0.9)',
    white70: 'rgba(255, 255, 255, 0.7)',
    white50: 'rgba(255, 255, 255, 0.5)',
    white30: 'rgba(255, 255, 255, 0.3)',
    white20: 'rgba(255, 255, 255, 0.2)',

    // This is needed because it's hard coded into the Radio component
    primary: 'black',

    // Missing
    red: 'rgba(255, 0, 0, 1)',
  },

  fontSizes: {
    base: 15,
    title: 18,
    caption: 12,
  },

  fontWeights: {
    body: 400,
    heading: 600,
    bold: 600,
  },

  space: [0, 2, 8, 12, 16, 24, 32, 48, 64, 128, 256, 512],

  flex: {
    center: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    verticalCenter: {
      display: 'flex',
      alignItems: 'center',
    },
    betweenCenter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },

  lineHeights: {
    body: 1.5,
    heading: 1.3,
  },

  letterSpacings: {
    body: 'normal',
    caps: '0.04em',
  },

  zIndices: {
    topContent: 50,
    header: 100,
    dropdown: 200,
    drawer: 500,
    modal: 1000,
  },

  breakpoints: [mobileBreakpoint, tabletBreakpoint],

  styles: {
    root: {
      variant: 'text.base',
      backgroundColor: 'surfaceGray',
    },
  },

  sizes: {
    appContainerWidth,
    desktopSidebarWidth,
    navHeight,
  },

  grids: {
    header: {
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: [
        '60px 1fr',
        '60px 1fr',
        `${desktopSidebarWidth} 1fr`,
      ],
      gridGap: 0,
      width: '100%',
      height: navHeight,
      '> *': {
        pr: 4,
      },
    },
    accountPage: {
      display: 'grid',
      gridTemplateColumns: [
        '40px 1fr',
        '100px 1fr',
        `${desktopSidebarWidth} 1fr`,
      ],
      gridGap: 0,
      maxWidth: appContainerWidth,
      mx: 'auto',
      px: 3,
    },
    sectionPicker: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gridGap: 1,
    },
  },

  text: {
    base: {
      fontFamily: 'body',
      fontSize: 'base',
      fontWeight: 'body',
      lineHeight: 'body',
      color: 'black',
    },
    baseDark: {
      variant: 'text.base',
      color: 'white',
    },
    baseEmphasis: {
      variant: 'text.base',
      fontWeight: 'bold',
    },
    baseEmphasisDark: {
      variant: 'text.baseEmphasis',
      color: 'white',
    },
    title: {
      variant: 'text.base',
      fontSize: 'title',
    },
    titleDark: {
      variant: 'text.title',
      color: 'white',
    },
    titleEmphasis: {
      variant: 'text.title',
      fontWeight: 'bold',
    },
    titleEmphasisDark: {
      variant: 'text.titleEmphasis',
      color: 'white',
    },
    listHeading: {
      variant: 'text.base',
      color: 'black50',
      lineHeight: '32px',
    },
    listHeadingDark: {
      variant: 'text.base',
      color: 'white50',
      lineHeight: '32px',
    },
    caption: {
      variant: 'text.base',
      fontSize: 'caption',
    },
    captionDark: {
      variant: 'text.caption',
      color: 'white',
    },
  },

  forms: {
    input: {
      border: '2px solid transparent',
      borderRadius: 0,
      backgroundColor: 'white',
      color: 'black',
      padding: 3,
      mb: 1,
      lineHeight: 'body',
      outline: 'none',
      '::placeholder': {
        color: 'black50',
      },
      ':focus': {
        borderColor: 'black',
      },
    },
    inputError: {
      variant: 'forms.input',
      borderColor: 'rgba(142, 0, 254, 0.1) !important',
    },
    inputErrorActive: {
      variant: 'forms.inputError',
      borderColor: 'rgba(142, 0, 254, 1) !important',
    },
    inline: {
      variant: 'input',
      display: 'block',
      width: '100%',
      backgroundColor: 'transparent',
      border: 'none',
      pl: 0,
      lineHeight: 2,
      fontWeight: 'bold',
      borderRadius: 0,
      outline: 'none',
      transition: 'background 50ms linear, padding 50ms linear',
      ':focus': {
        backgroundColor: 'buttonGray',
        pl: 3,
      },
    },
    emphasisInput: {
      variant: 'forms.input',
      fontWeight: 'bold',
    },
    textarea: {
      variant: 'forms.input',
      display: 'block',
      overflow: 'hidden',
      resize: 'none',
    },
    textareaError: {
      variant: 'forms.textarea',
      borderColor: 'rgba(255, 0, 0, 0.3) !important',
    },
    textareaErrorActive: {
      variant: 'forms.textareaError',
      borderColor: 'rgba(255, 0, 0, 0.9) !important',
    },
    radio: {
      flexShrink: 0,
      color: 'black',
    },
    select: {
      color: 'black',
      border: 'none',
      outlineColor: 'black',
      ':disabled': {
        color: 'black50',
      },
    },
    selectDark: {
      color: 'white',
      backgroundColor: 'buttonGrayDark',
      border: 'none',
    },
    selectError: {
      variant: 'forms.select',
      outlineColor: 'rgba(142, 0, 254, 1)',
    },
    slider: {
      thumb: {
        color: 'white',
      },
    },
    label: {
      variant: 'text.caption',
      display: 'block',
      color: 'black50',
    },
  },

  buttons: {
    primary: {
      variant: 'text.baseEmphasis',
      width: '100%',
      color: 'white',
      backgroundColor: 'blue',
      borderRadius: 0,
      padding: 4,
      display: 'flex',
      justifyConent: 'flex-start',
      alignItems: 'center',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s linear',
      ':disabled': {
        backgroundColor: 'black30',
      },
      ':focus': {
        outlineColor: 'black',
        outlineStyle: 'solid',
        outlineWidth: 2,
      },
    },
    primaryDark: {
      variant: 'buttons.primary',
      backgroundColor: 'blueDark',
      color: 'black',
    },
    secondary: {
      variant: 'buttons.primary',
      backgroundColor: 'buttonGray',
      color: 'black',
    },
    secondaryDark: {
      variant: 'buttons.primary',
      backgroundColor: 'buttonGrayDark',
      color: 'white',
    },
    link: {
      variant: 'buttons.primary',
      width: 'auto',
      fontWeight: 'body',
      backgroundColor: 'transparent',
      outline: 'none !important',
      color: 'blue',
      ':disabled': {
        backgroundColor: 'transparent',
      },
    },
    sidebarControl: {
      variant: 'buttons.primary',
      backgroundColor: 'surfaceGrayDark',
      color: 'white',
      p: 3,
      outline: 'none !important',
    },
    icon: {
      padding: 0,
      cursor: 'pointer',
      outline: 'none !important',
    },
    tileIcon: {
      variant: 'buttons.primary',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'buttonGray',
      fontWeight: 'body',
      fontSize: 0,
      width: 'auto',
      px: 1,
      outline: 'none !important',
    },
    // Remove all button styling
    minimal: {
      backgroundColor: 'transparent',
      width: 'auto',
      padding: 0,
      cursor: 'pointer',
      outline: 'none !important',
    },
    noStyles: {
      padding: 0,
      appearance: 'none',
      border: 'none',
      lineHeight: '1',
      outline: 'none',
      userSelect: 'none',
      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
      cursor: 'pointer',
      textAlign: 'start',
      backgroundColor: 'transparent',
      color: 'inherit',
    },
  },

  links: {
    default: {
      variant: 'text.baseEmphasis',
      textDecoration: 'none',
      color: 'blue',
      outline: 'none',
    },
    primaryButton: {
      variant: 'buttons.primary',
    },
    secondaryButton: {
      variant: 'buttons.secondary',
    },
    setupLink: {
      display: 'flex',
      width: '100%',
      borderColor: 'buttonGray',
      borderStyle: 'solid',
      borderWidth: 1,
      py: 16,
      px: 24,
      alignItems: 'center',
      justifyContent: 'space-between',
      textDecoration: 'none',
      color: 'black',
      transition: 'all 300ms',
      svg: {
        transition: 'transform 300ms',
      },
      'svg path': {
        transition: 'stroke 300ms',
      },
      ':hover': {
        borderColor: 'blue',
        color: 'blue',
        svg: {
          transform: 'translateX(10px)',
        },
        'svg path': {
          stroke: 'blue',
        },
      },
    },
  },

  images: {
    avatar: {
      width: 48,
      height: 48,
      borderRadius: 99999,
    },
  },

  containers: {
    indentedCell: {
      variant: 'containers.cell',
      display: ['none', 'flex'],
      pl: 0,
      backgroundColor: ['transparent', 'transparent'],
    },
  },
};

export default theme;
